import { ConsoleSink } from './ConsoleSink'
import { type ILogger, Logger, type LoggerHost } from './Logger'
import { LogLevel, type LogLevelName, parseLogLevel } from './LogLevel'
import type { ILogSink, LoggingConfig, LogRecord } from './types'

/** memory 诊断默认按需开启，避免继承全局 WARN/DEBUG 后污染正常控制台输出。 */
function isOptInMemoryNamespace(namespace: string): boolean {
  return (
    namespace === 'memory' ||
    namespace.startsWith('memory:') ||
    namespace.endsWith(':memory') ||
    namespace.includes(':memory:')
  )
}

/** 默认时钟：优先 performance.now，缺失时回退 0（不影响过滤逻辑） */
function createClock(): () => number {
  const perf = (globalThis as { performance?: { now?: () => number } }).performance
  if (perf != null && typeof perf.now === 'function') {
    return () => perf.now!()
  }
  return () => 0
}

/**
 * 全局日志管理器：持有默认级别与命名空间覆盖表，缓存 Logger 实例，持有唯一 ConsoleSink，
 * 并接收 Worker 回传记录转发到 sink。命名空间级别采用前缀匹配（精确优先，其余按最长前缀命中）。
 */
export class LoggerManager implements LoggerHost {
  private _defaultLevel: LogLevel
  private _perf: boolean
  private readonly _namespaceLevels = new Map<string, LogLevel>()
  private readonly _loggers = new Map<string, ILogger>()
  private _sink: ILogSink
  private readonly _now = createClock()

  /**
   * @param config - 日志配置，缺省时使用 defaultLevel；config.sink 缺省时回退 ConsoleSink
   * @param defaultLevel - 未配置 level 时的默认级别（dev=WARN / prod=SILENT 由调用方决定）
   * @param sink - 显式 sink，优先级高于 config.sink，主要供测试注入
   */
  constructor(config?: LoggingConfig, defaultLevel: LogLevel = LogLevel.WARN, sink?: ILogSink) {
    this._sink = sink ?? config?.sink ?? new ConsoleSink()
    this._defaultLevel = LogLevel.WARN
    this._perf = false
    this.configure(config, defaultLevel)
  }

  /**
   * 应用日志配置，可在运行期重复调用。复用同一实例以保证已捕获的 Logger 引用持续有效。
   *
   * sink 的处理是惰性单例时序的关键：仅当 config.sink 显式提供时才替换，未提供则保留现有 sink。
   * 这样宿主在 createPotreeEngine 阶段注入的 sink 能覆盖此前 getLoggerManager() 惰性建出的默认
   * ConsoleSink；而未注入 sink 的后续 configure 调用不会把已注入的宿主 sink 冲掉。
   *
   * @param config - 日志配置
   * @param defaultLevel - 未配置 level 时的默认级别
   */
  configure(config?: LoggingConfig, defaultLevel: LogLevel = LogLevel.WARN): void {
    this._defaultLevel = parseLogLevel(config?.level, defaultLevel)
    this._perf = config?.perf ?? false
    if (config?.sink != null) {
      this._sink = config.sink
    }
    this._namespaceLevels.clear()
    if (config?.namespaces != null) {
      for (const ns of Object.keys(config.namespaces)) {
        this._namespaceLevels.set(ns, parseLogLevel(config.namespaces[ns], this._defaultLevel))
      }
    }
  }

  // ===== LoggerHost 实现 =====

  now(): number {
    return this._now()
  }

  isPerfEnabled(): boolean {
    return this._perf
  }

  /**
   * 判断命名空间是否输出该级别。
   *
   * @param namespace - 命名空间
   * @param level - 待判断级别
   * @returns 会输出返回 true
   */
  isEnabled(namespace: string, level: LogLevel): boolean {
    return level <= this._resolveLevel(namespace)
  }

  /**
   * 经级别过滤后输出（Logger 已预过滤，这里直接写入）。
   *
   * @param record - 日志记录
   */
  emit(record: LogRecord): void {
    this._sink.write(record)
  }

  /**
   * 不经级别过滤直接写入 sink，供 perf 输出使用。
   *
   * @param record - 日志记录
   */
  writeRecord(record: LogRecord): void {
    this._sink.write(record)
  }

  /**
   * 获取或创建命名空间 Logger，实例按命名空间缓存。
   *
   * @param namespace - 命名空间
   * @returns Logger 实例
   */
  getLogger(namespace: string): ILogger {
    let logger = this._loggers.get(namespace)
    if (logger == null) {
      logger = new Logger(namespace, this)
      this._loggers.set(namespace, logger)
    }
    return logger
  }

  // ===== 运行时控制 API =====

  /** root logger，命名空间为空串，宿主一般用 child 派生 */
  get root(): ILogger {
    return this.getLogger('')
  }

  /**
   * 设置全局默认级别。
   *
   * @param level - 新的默认级别，支持枚举或字符串别名
   */
  setLevel(level: LogLevel | LogLevelName): void {
    this._defaultLevel = parseLogLevel(level, this._defaultLevel)
  }

  /**
   * 设置命名空间级别覆盖（前缀匹配生效）。
   *
   * @param namespace - 命名空间
   * @param level - 级别，支持枚举或字符串别名
   */
  setNamespaceLevel(namespace: string, level: LogLevel | LogLevelName): void {
    this._namespaceLevels.set(namespace, parseLogLevel(level, this._defaultLevel))
  }

  /**
   * 开关性能埋点。
   *
   * @param enabled - 是否启用 perf
   */
  setPerfEnabled(enabled: boolean): void {
    this._perf = enabled
  }

  /**
   * 运行期替换日志输出 sink，立即对所有已缓存的 Logger 生效。
   *
   * 供宿主在 createPotreeEngine 之外的时机（如拿到 engine 实例后）接管日志输出；
   * 主路径仍建议通过 EngineConfig.logging.sink 在初始化阶段注入。
   *
   * @param sink - 新的日志 sink
   */
  setSink(sink: ILogSink): void {
    this._sink = sink
  }

  /**
   * 接收 Worker 回传的日志记录，按级别过滤后输出。
   *
   * @param record - Worker 端构造的日志记录
   */
  receiveRecord(record: LogRecord): void {
    if (this.isEnabled(record.namespace, record.level)) {
      this._sink.write(record)
    }
  }

  /**
   * 解析命名空间的有效级别：精确命中优先，其次最长前缀命中，最后全局默认。
   *
   * @param namespace - 命名空间
   * @returns 有效级别
   */
  private _resolveLevel(namespace: string): LogLevel {
    let key = namespace
    while (key.length > 0) {
      const hit = this._namespaceLevels.get(key)
      if (hit !== undefined) {
        return hit
      }
      const idx = key.lastIndexOf(':')
      if (idx < 0) {
        break
      }
      key = key.slice(0, idx)
    }
    if (isOptInMemoryNamespace(namespace)) {
      return LogLevel.SILENT
    }
    return this._defaultLevel
  }
}

let globalManager: LoggerManager | null = null

/**
 * 创建并设为全局单例的 LoggerManager。重复调用会替换全局实例。
 *
 * @param config - 日志配置
 * @param defaultLevel - 未配置 level 时的默认级别
 * @returns LoggerManager 实例
 */
export function createLoggerManager(
  config?: LoggingConfig,
  defaultLevel: LogLevel = LogLevel.WARN
): LoggerManager {
  if (globalManager == null) {
    globalManager = new LoggerManager(config, defaultLevel)
  } else {
    globalManager.configure(config, defaultLevel)
  }
  return globalManager
}

/**
 * 获取全局 LoggerManager，未初始化时惰性创建一个默认实例。
 *
 * 用于 EventBus 等早于 createPotreeEngine 的场景，避免日志丢失。
 *
 * @returns 全局 LoggerManager
 */
export function getLoggerManager(): LoggerManager {
  if (globalManager == null) {
    globalManager = new LoggerManager()
  }
  return globalManager
}
